import {FIELD_BIOME_LAYER_KEYS, FIELD_BIOME_TEXTURES} from '../../../configs/fieldBiomeEffects';
import {WIND_VEILS_KIND} from '../../../configs/biomeEffects/windVeils';
import {resolveSpawnBox} from './biomeContext';
import {createGustLeaves, resolveGustLeafSway, writeGustLeafPose} from './gustLeaves';
import {
  TWO_PI,
  hash01,
  pickPointInBox,
  randomIn,
  randomInt,
  randomRange,
  randomSign,
  resolveLifeEnvelope,
  resolveOvalFade,
  smoothstep,
  writeHiddenPose,
  writePose,
} from './biomeMath';

/**
 * ВУАЛИ ВЕТРА (соляная подложка, два луга и плато), ЛИСТЬЯ (только луга) И
 * МИРАЖИ (только соляная: см. configs/biomeEffects/windVeils). Чистые функции без Phaser и DOM: по
 * этому описанию частицы каждый кадр ставит сцена (scenes/FieldBiomeEffects).
 *
 * ВУАЛЬ — пучок прядей, летящий по ветру матча (тому же, что несет облака).
 * Прядь — цепочка коротких штрихов, уложенных внахлест вдоль своего русла; в
 * кадре штриха четыре тонких волоска и дымка между ними (текстура TRESS), так
 * что вуаль — две дюжины тонких линий. Русло НЕПОДВИЖНО в пространстве: место
 * штриха поперек пути зависит только от того, где он сейчас вдоль пути. Прядь
 * не едет жесткой загогулиной, а течет по извилистой дорожке, и каждый штрих
 * лежит по касательной к ней.
 *
 * Русло пряди — три слагаемых:
 *  - общая волна вуали (у каждой пряди — свой размах);
 *  - место пряди поперек вуали, умноженное на «разворот» вуали в этом месте
 *    пути. Вуаль перекручена, как лента: разворот — косинус той же волны, у
 *    гребней он проходит через ноль. Там пряди сходятся в жгут и меняются
 *    местами, между гребнями — расходятся веером. Где пряди сошлись, их
 *    плотности складываются: складка вуали выходит яркой;
 *  - своя медленная волна пряди.
 * Вместе с прядями в складке сходятся и волоски каждой пряди: кадр штриха там
 * сжимается, а сам штрих плотнее.
 *
 * Часть прядей вуали — темные (цвета тени) и лежат под светлыми: на светлой
 * земле вуаль выдают они.
 *
 * Насколько вуаль изогнута, решают числа подложки: размах и длина общей волны
 * и ширина веера прядей. У плато волна ниже и длиннее — вуаль там площе.
 *
 * У подложки может быть СВОЯ ЗЕМЛЯ вуалей (area) — овал на картинке пейзажа:
 * луг, вершина плато. К кромке овала штрихи тают, за ней их нет: вуаль не
 * летит над лесом, морем и пропастью за обрывом. Не задана — вуали видны
 * везде, где идет их путь (так на соли).
 *
 * ЛИСТЬЯ — в группе своей вуали, после всех ее штрихов (числа прядей от
 * листьев не зависят: то же зерно дает на лугу ту же вуаль, что на соли). Лист
 * (./gustLeaves — тот же, что у порыва над лугом) идет путем вуали, вокруг ее
 * общей волны, чуть быстрее или медленнее ее, проступает и тает на тех же
 * отрезках пути. Листья — по числам подложки (leaves): нет чисел — нет листьев.
 *
 * Дорожка вуалей помечена (WIND_VEILS_TAG): по метке сцена находит живые
 * вуали и, когда вуаль в кадре игрока, играет шум ветра (см.
 * FieldBiomeEffects.findLiveGroups). Рождаться вуалям метка не мешает: сцена
 * их не придерживает.
 *
 * МИРАЖ — несколько плоских линз цвета бледного неба внахлест («вода») и
 * тонкие горизонтальные блики на них. Линзы дрожат, блики вспыхивают, скользят
 * и гаснут, каждый раз на новом месте. Лежит мираж на самой земле (слой
 * GROUND): под сеткой ячеек, их дымкой и жетонами, — и только на соляной
 * корке. Ветер матча миражу не нужен. Миражи — по числам подложки (mirages):
 * нет чисел — нет и дорожки миражей.
 */

/** Метка дорожки вуалей: по ней хозяин контроллера находит живые вуали */
export const WIND_VEILS_TAG = 'windVeils';

const LIGHT = 'light';
const SHADE = 'shade';
const LEAF = 'leaf';
// Порядок в слое: темные пряди (0), светлые (1), листья (2)
const LEAF_SUBLAYER = 2;
const POOL = 'pool';
const GLITTER = 'glitter';

/** Средняя часть прямоугольника: такая доля его ширины и высоты вокруг середины */
const shrinkBox = (box, share) => {
  const marginX = ((box.right - box.left) * (1 - share)) / 2;
  const marginY = ((box.bottom - box.top) * (1 - share)) / 2;
  return {left: box.left + marginX, right: box.right - marginX, top: box.top + marginY, bottom: box.bottom - marginY};
};

// --- вуали

const createVeilTrack = (context, params) => {
  const {hexSize, wind} = context;
  const {veils, strand, leaves} = params;
  const across = {x: -wind.y, y: wind.x};
  // Своя земля вуалей на картинке пейзажа; не задана — вуали видны везде
  const area = params.area ? context.toWorldOval(params.area) : null;

  /** Место вдоль общей волны вуали (радианы) в данном месте пути */
  const resolveWaveAngle = (particle, along) => (TWO_PI * along) / particle.waveLength + particle.wavePhase;
  /** Место вдоль своей медленной волны пряди */
  const resolveRippleAngle = (particle, along) => (TWO_PI * along) / particle.rippleLength + particle.ripplePhase;
  /**
   * Разворот вуали для пряди в данном месте пути: 1 и -1 — вуаль расправлена
   * (пряди лежат веером, по разные стороны от складки — в обратном порядке),
   * 0 — складка
   */
  const resolveSpread = (particle, along) => Math.cos(resolveWaveAngle(particle, along) + particle.foldShift);
  /** Отклонение русла пряди поперек пути в данном месте пути; русло неподвижно в пространстве */
  const resolveLaneOffset = (particle, along) => (
    particle.waveAmplitude * Math.sin(resolveWaveAngle(particle, along))
    + particle.lane * resolveSpread(particle, along)
    + particle.rippleAmplitude * Math.sin(resolveRippleAngle(particle, along))
  );
  /** Наклон русла к пути в данном месте: производная отклонения */
  const resolveLaneSlope = (particle, along) => (
    (TWO_PI / particle.waveLength) * (
      particle.waveAmplitude * Math.cos(resolveWaveAngle(particle, along))
      - particle.lane * Math.sin(resolveWaveAngle(particle, along) + particle.foldShift)
    )
    + particle.rippleAmplitude * (TWO_PI / particle.rippleLength) * Math.cos(resolveRippleAngle(particle, along))
  );

  /** Какие пряди вуали темные: через равное число прядей, начиная со случайной */
  const pickShadeIndexes = (rng) => {
    const shadeCount = Math.min(strand.shadeCount, strand.count);
    const indexes = new Set();
    if (shadeCount > 0) {
      const stride = Math.floor(strand.count / shadeCount);
      const first = randomInt(rng, 0, stride - 1);
      for (let order = 0; order < shadeCount; order += 1) {
        indexes.add(first + order * stride);
      }
    }
    return indexes;
  };

  const buildGroup = ({rng, view}) => {
    // Середина пути вуали — там, куда смотрит камера, в средней части кадра
    const through = pickPointInBox(rng, shrinkBox(
      resolveSpawnBox(context, view, {fieldMarginFactor: veils.fieldMarginFactor, viewMarginFactor: 0}),
      veils.viewCoreShare
    ));
    const travel = hexSize * randomIn(rng, veils.travelFactor);
    const speed = hexSize * randomIn(rng, veils.speedFactor);
    const length = hexSize * randomIn(rng, veils.lengthFactor);
    const halfWidth = hexSize * randomIn(rng, veils.halfWidthFactor);
    // Общая волна русел вуали
    const flow = {
      amplitude: hexSize * randomIn(rng, veils.waveAmplitudeFactor),
      length: hexSize * randomIn(rng, veils.waveLengthFactor),
      phase: rng() * TWO_PI,
    };
    const from = -travel / 2;
    const to = travel / 2;

    const shadeIndexes = pickShadeIndexes(rng);
    const particles = [];
    let durationMs = 0;
    for (let index = 0; index < strand.count; index += 1) {
      const isShade = shadeIndexes.has(index);
      const paint = isShade ? params.shade : params.light;
      const strandLength = length * randomIn(rng, strand.lengthShare);
      const base = {
        // Над отрядами и их значками, под тенями облаков и облаками (решение владельца игры)
        layer: FIELD_BIOME_LAYER_KEYS.AIR,
        // Темные пряди — под светлыми
        sublayer: isShade ? 0 : 1,
        texture: FIELD_BIOME_TEXTURES.TRESS,
        tint: paint.tint,
        part: isShade ? SHADE : LIGHT,
        strandIndex: index,
        // Места прядей поперек вуали — по порядку, каждое со своим разбросом:
        // вся ширина занята, но не по линейке
        lane: halfWidth * (((index + rng()) / strand.count) * 2 - 1),
        // Прядь короче вуали и лежит в ней на случайном месте: концы вуали редеют
        lag: rng() * (length - strandLength),
        length: strandLength,
        segmentLength: (strandLength / strand.segmentCount) * strand.segmentOverlap,
        speed: speed * randomIn(rng, strand.speedShare),
        waveAmplitude: flow.amplitude * randomIn(rng, strand.waveAmplitudeShare),
        waveLength: flow.length,
        wavePhase: flow.phase,
        foldShift: randomIn(rng, strand.foldShiftRad),
        rippleAmplitude: hexSize * randomIn(rng, strand.rippleAmplitudeFactor),
        rippleLength: flow.length * randomIn(rng, strand.rippleLengthShare),
        ripplePhase: rng() * TWO_PI,
        thickness: hexSize * randomIn(rng, strand.thicknessFactor),
        pulsePeriodMs: randomIn(rng, strand.pulsePeriodMs),
        pulsePhase: rng() * TWO_PI,
      };
      const alpha = paint.alpha * randomIn(rng, strand.alphaShare);
      for (let segment = 0; segment < strand.segmentCount; segment += 1) {
        // Место штриха в пряди: 0 — хвост, 1 — голова
        const bodyShare = (segment + 0.5) / strand.segmentCount;
        particles.push({
          ...base,
          bodyShare,
          // К обоим концам прядь сходит на нет
          segmentAlpha: alpha * Math.sin(Math.PI * bodyShare) ** strand.taperPower,
        });
      }
      // Вуаль живет, пока хвост самой отставшей пряди не дойдет до конца пути
      durationMs = Math.max(durationMs, ((to - from + base.lag + base.length) / base.speed) * 1000);
    }
    // Листья (на лугах) — после всех штрихов: на числа прядей они не влияют
    if (leaves) {
      const pathSeconds = durationMs / 1000;
      createGustLeaves(rng, leaves, hexSize).forEach((leaf) => {
        // Место листа в начале пути: от головы вуали (ноль) до ее хвоста (вся ее длина)
        const back = (0.5 - leaf.alongShare) * length;
        particles.push({
          ...leaf,
          part: LEAF,
          // Вуаль идет в том же слое, что и листья: листья — поверх ее прядей
          sublayer: LEAF_SUBLAYER,
          back,
          // Лист чуть быстрее или медленнее вуали, но ее не переживает: к концу ее срока он уже прошел весь путь
          speed: Math.max(speed * leaf.speedShare, (to - from + back) / pathSeconds),
        });
      });
    }
    return {durationMs, through, from, to, fade: hexSize * veils.fadeFactor, flow, halfWidth, length, particles};
  };

  /** Поза листа: идет путем своей вуали, вокруг ее общей волны, проступает и тает на тех же отрезках пути */
  const resolveLeafPose = (group, particle, elapsedMs, out) => {
    const along = group.from - particle.back + (particle.speed * elapsedMs) / 1000;
    const pathFade = Math.min(
      smoothstep(group.from, group.from + group.fade, along), smoothstep(group.to, group.to - group.fade, along)
    );
    if (!(pathFade > 0)) {
      return writeHiddenPose(out);
    }
    const {flow} = group;
    const offset = flow.amplitude * Math.sin((TWO_PI * along) / flow.length + flow.phase)
      + particle.acrossShare * hexSize * leaves.acrossFactor + resolveGustLeafSway(particle, elapsedMs);
    return writeGustLeafPose(
      out,
      leaves,
      particle,
      group.through.x + wind.x * along + across.x * offset,
      group.through.y + wind.y * along + across.y * offset,
      leaves.alpha * pathFade,
      elapsedMs
    );
  };

  /**
   * Поза штриха пряди. Путь вуали: от `from` до `to` вдоль ветра через точку
   * `through`; на отрезках `fade` в начале и в конце пути вуаль проступает и тает
   */
  const resolvePose = (group, particle, elapsedMs, out) => {
    if (!(elapsedMs > 0) || !(elapsedMs < group.durationMs)) {
      return writeHiddenPose(out);
    }
    if (particle.part === LEAF) {
      return resolveLeafPose(group, particle, elapsedMs, out);
    }
    // Голова пряди идет от начала пути, штрих — на своем месте позади нее
    const headAlong = group.from - particle.lag + (particle.speed * elapsedMs) / 1000;
    const along = headAlong - (1 - particle.bodyShare) * particle.length;
    const pathFade = Math.min(
      smoothstep(group.from, group.from + group.fade, along), smoothstep(group.to, group.to - group.fade, along)
    );
    if (!(pathFade > 0)) {
      return writeHiddenPose(out);
    }
    // Русло неподвижно: отклонение поперек пути — функция места вдоль пути
    const offset = resolveLaneOffset(particle, along);
    const x = group.through.x + wind.x * along + across.x * offset;
    const y = group.through.y + wind.y * along + across.y * offset;
    // К кромке своей земли штрих тает, за ней его нет (без своей земли — единица)
    const areaFade = resolveOvalFade(area, x, y, params.areaFade);
    if (!(areaFade > 0)) {
      return writeHiddenPose(out);
    }

    const pulse = 1 - strand.pulseDepth
      * (0.5 + 0.5 * Math.sin((TWO_PI * elapsedMs) / particle.pulsePeriodMs + particle.pulsePhase));
    // В складке вуали волоски пряди сходятся: кадр штриха сжимается — плавно, без излома в самой складке
    const spread = resolveSpread(particle, along);
    const thin = strand.foldThicknessShare;
    const thickness = particle.thickness * Math.sqrt(thin * thin + (1 - thin * thin) * spread * spread);
    // Та же пыль в складке лежит уже и плотнее, в расправленной вуали — шире и бледнее
    const density = 1 - (1 - strand.spreadAlphaShare) * spread * spread;
    // Штрих лежит по касательной к руслу. Вверх ногами его не кладут: порядок
    // волосков в кадре не должен зависеть от того, куда дует ветер, —
    // направление берется «слева направо»
    const slope = resolveLaneSlope(particle, along);
    const tangentX = wind.x + across.x * slope;
    const tangentY = wind.y + across.y * slope;
    const facing = tangentX < 0 ? -1 : 1;
    // Штрихи расставлены ровно вдоль пути, и на наклонном участке русла они
    // расходятся: кадр штриха вытягивается следом (до своего потолка) —
    // плотность вдоль пряди остается ровной
    const length = Math.min(
      particle.segmentLength * Math.sqrt(1 + slope * slope), hexSize * strand.maxSegmentLengthFactor
    );
    return writePose(
      out, x, y, particle.segmentAlpha * density * pulse * pathFade * areaFade, length, thickness,
      Math.atan2(tangentY * facing, tangentX * facing)
    );
  };

  return {
    schedule: {
      firstDelayMs: veils.firstDelayMs,
      intervalMs: veils.intervalMs,
      maxGroups: veils.maxGroups,
      prewarm: veils.prewarm,
    },
    tag: WIND_VEILS_TAG,
    buildGroup,
    resolvePose,
  };
};

// --- миражи

const createMirageTrack = (context, params) => {
  const {hexSize} = context;
  const {mirages} = params;
  const {pool, glitter} = mirages;
  const pan = context.toWorldOval(params.pan);

  /** Место миража: на корке, там, куда смотрит камера, поодаль от других миражей; null — не нашлось */
  const pickCentre = (rng, view, liveGroups) => {
    const box = resolveSpawnBox(context, view, {fieldMarginFactor: mirages.fieldMarginFactor, viewMarginFactor: 0});
    const minGap = hexSize * mirages.minGapFactor;
    for (let attempt = 0; attempt < mirages.placeAttempts; attempt += 1) {
      const point = pickPointInBox(rng, box);
      const isOnPan = resolveOvalFade(pan, point.x, point.y, params.panFade) >= 1;
      const isApart = liveGroups.every(({centre}) => Math.hypot(centre.x - point.x, centre.y - point.y) >= minGap);
      if (isOnPan && isApart) {
        return point;
      }
    }
    return null;
  };

  const buildGroup = ({rng, view, liveGroups}) => {
    const centre = pickCentre(rng, view, liveGroups);
    if (!centre) {
      return null;
    }
    const width = hexSize * randomIn(rng, pool.widthFactor);
    const height = width * randomIn(rng, pool.flatness);
    const particles = [];
    for (let lobe = 0; lobe < pool.lobeCount; lobe += 1) {
      // Первая линза — средняя, остальные — мельче, по очереди справа и слева от нее
      const isMiddle = lobe === 0;
      const scale = isMiddle ? 1 : randomIn(rng, pool.sideScale);
      const side = lobe % 2 === 1 ? 1 : -1;
      particles.push({
        layer: FIELD_BIOME_LAYER_KEYS.GROUND,
        sublayer: 0,
        texture: FIELD_BIOME_TEXTURES.LENS,
        tint: pool.tint,
        part: POOL,
        lobe,
        dx: isMiddle ? 0 : side * width * randomIn(rng, pool.sideShiftShare),
        dy: isMiddle ? 0 : height * randomRange(rng, -pool.sideDropShare, pool.sideDropShare),
        width: width * scale,
        height: height * scale,
        tremblePeriodMs: randomIn(rng, pool.tremblePeriodMs),
        tremblePhase: rng() * TWO_PI,
        shimmerPeriodMs: randomIn(rng, pool.shimmerPeriodMs),
        shimmerPhase: rng() * TWO_PI,
      });
    }
    for (let index = 0; index < glitter.count; index += 1) {
      particles.push({
        layer: FIELD_BIOME_LAYER_KEYS.GROUND,
        // Блики — поверх «воды»
        sublayer: 1,
        texture: FIELD_BIOME_TEXTURES.STREAK,
        tint: glitter.tint,
        part: GLITTER,
        length: hexSize * randomIn(rng, glitter.lengthFactor),
        thickness: hexSize * randomIn(rng, glitter.thicknessFactor),
        cycleMs: randomIn(rng, glitter.cycleMs),
        // Блики вспыхивают вразнобой: у каждого свой сдвиг по времени
        cycleOffsetMs: rng() * glitter.cycleMs.max,
        slide: hexSize * randomIn(rng, glitter.slideFactor) * randomSign(rng),
        // Свое зерно блика: по нему и номеру вспышки — ее место
        seed: rng() * 1000,
      });
    }
    return {
      durationMs: randomIn(rng, mirages.durationMs),
      centre,
      // Мираж медленно плывет вбок
      driftSpeed: hexSize * randomIn(rng, mirages.driftFactor) * randomSign(rng),
      width,
      height,
      particles,
    };
  };

  const resolvePose = (group, particle, elapsedMs, out) => {
    const envelope = resolveLifeEnvelope(elapsedMs / group.durationMs, mirages.fadeInShare, mirages.fadeOutShare);
    if (!(envelope > 0)) {
      return writeHiddenPose(out);
    }
    const centreX = group.centre.x + (group.driftSpeed * elapsedMs) / 1000;
    const centreY = group.centre.y;

    if (particle.part === POOL) {
      // Дрожь: линза то шире и площе, то уже и выше, и плотность ее проседает — у каждой линзы в своем темпе
      const tremble = pool.trembleDepth
        * Math.sin((TWO_PI * elapsedMs) / particle.tremblePeriodMs + particle.tremblePhase);
      const shimmer = 1 - pool.shimmerDepth
        * (0.5 + 0.5 * Math.sin((TWO_PI * elapsedMs) / particle.shimmerPeriodMs + particle.shimmerPhase));
      return writePose(
        out, centreX + particle.dx, centreY + particle.dy, pool.alpha * envelope * shimmer,
        particle.width * (1 + tremble), particle.height * (1 - tremble), 0
      );
    }

    // Блик: вспышка за вспышкой, каждая — на новом месте средней линзы. Место —
    // от номера вспышки и зерна блика: поза остается функцией времени
    const cycleTime = (elapsedMs + particle.cycleOffsetMs) / particle.cycleMs;
    const cycle = Math.floor(cycleTime);
    const cycleShare = cycleTime - cycle;
    const angle = hash01(cycle, particle.seed) * TWO_PI;
    const distance = Math.sqrt(hash01(cycle, particle.seed + 1)) * glitter.reachShare;
    const flicker = Math.sin(Math.PI * cycleShare);
    return writePose(
      out,
      // За вспышку блик проходит свой путь вбок: середина пути — место вспышки
      centreX + Math.cos(angle) * distance * (group.width / 2) + particle.slide * (cycleShare - 0.5),
      centreY + Math.sin(angle) * distance * (group.height / 2),
      glitter.alpha * envelope * flicker,
      // Вспыхивая, блик вытягивается, угасая — сжимается
      particle.length * (0.5 + 0.5 * flicker),
      particle.thickness,
      0
    );
  };

  return {
    schedule: {firstDelayMs: mirages.firstDelayMs, intervalMs: mirages.intervalMs, maxGroups: mirages.maxGroups},
    // Мираж лежит на земле: при смене ветра матча он остается как был
    usesWind: false,
    buildGroup,
    resolvePose,
  };
};

/** Дорожки подложки: вуали (на лугах — с листьями) — у всех, миражи — у той, которой заданы их числа (соляная) */
const createTracks = (context, params) => (
  params.mirages
    ? [createVeilTrack(context, params), createMirageTrack(context, params)]
    : [createVeilTrack(context, params)]
);

const windVeils = {
  kind: WIND_VEILS_KIND,
  // Линза и штрих нужны только миражам, лист — только лугам
  textures: [
    FIELD_BIOME_TEXTURES.TRESS, FIELD_BIOME_TEXTURES.LENS, FIELD_BIOME_TEXTURES.STREAK, FIELD_BIOME_TEXTURES.LEAF,
  ],
  createTracks,
};

export default windVeils;
